import { PartnerOperationId, PartnerOutcome, PartnerRecordKind } from "@/shared/api/partner-api-events"

// Значения перечислимы контрактом провайдера; неизвестное значение
// показываем как есть, а не прячем за общим словом.
const RECORD_KIND_LABELS: Record<PartnerRecordKind, string> = {
    attempt: "Попытка",
    outcome: "Результат",
    lifecycle: "Жизненный цикл ключа",
}

const OUTCOME_LABELS: Record<PartnerOutcome, string> = {
    success: "Успех",
    error: "Ошибка",
    pending: "Ожидает завершения",
}

// Названия операций контракта провайдера. Ключ — технический `operation_id`,
// который остаётся в таблице и в поле «Тип события» детали, поэтому русское
// имя ничего не прячет, а только даёт выбрать операцию, не помня её id.
const OPERATION_LABELS: Record<PartnerOperationId, string> = {
    "external.bonuses.list": "Список бонусов",
    "external.bonuses.balance": "Баланс бонусов пользователя",
    "external.bonuses.accrual": "Начисление бонусов",
    "external.partners.transactions-report": "Отчёт по транзакциям партнёра",
    "external.user-integrations.resolve": "Связка внешнего пользователя",
    "external.users.list": "Список пользователей",
    "external.users.get": "Карточка пользователя",
    "external.admin-audit.list": "Журнал действий администраторов",
    "external.iam-admins.list-delta": "Изменения администраторов (IAM)",
    "external.partner-api-events.list": "Журнал вызовов партнёрского API",
}

/**
 * `target_types` провайдер перечислением НЕ ограничивает — принимает любую
 * строку. Здесь перечислены все типы целей, которые сегодня порождают
 * операции контракта, поэтому список работает как подсказка, а не как замок:
 * значение вне его администратор добавляет вручную.
 */
const TARGET_TYPE_LABELS: Record<string, string> = {
    user: "Пользователь",
    bonus: "Бонус",
    partner: "Партнёр",
    external_user: "Внешний пользователь",
    group: "Группа",
    admin: "Администратор",
    api_key: "Ключ API",
    event_target: "Цель события журнала",
}

export const OPERATION_OPTIONS = (Object.keys(OPERATION_LABELS) as PartnerOperationId[])
    .map((value) => ({ value, label: OPERATION_LABELS[value] }))

export const TARGET_TYPE_OPTIONS = Object.keys(TARGET_TYPE_LABELS)
    .map((value) => ({ value, label: TARGET_TYPE_LABELS[value] }))

export const RECORD_KIND_OPTIONS = (Object.keys(RECORD_KIND_LABELS) as PartnerRecordKind[])
    .map((value) => ({ value, label: RECORD_KIND_LABELS[value] }))

export const OUTCOME_OPTIONS = (Object.keys(OUTCOME_LABELS) as PartnerOutcome[])
    .map((value) => ({ value, label: OUTCOME_LABELS[value] }))

export const resolveRecordKindLabel = (recordKind: string): string =>
    RECORD_KIND_LABELS[recordKind as PartnerRecordKind] ?? recordKind

/**
 * `null` — это НЕ «ожидает завершения». Провайдер пишет `outcome: null` в
 * завершённых lifecycle-записях (создание, включение/выключение, смена
 * разрешений, пересоздание ключа), а незавершённую попытку отдаёт явным
 * значением `pending`. Поэтому пустой исход показываем нейтрально.
 */
export const resolveOutcomeLabel = (outcome: string | null): string => {
    if (!outcome) return "—"

    return OUTCOME_LABELS[outcome as PartnerOutcome] ?? outcome
}

export const resolveActorKindLabel = (actorKind: string): string => {
    if (actorKind === "partner_key") return "Партнёрский ключ"
    if (actorKind === "admin") return "Администратор"

    return actorKind
}
